import { Validation } from 'components/Utils/Validation';
import { forwardRef } from 'react';
import {
  VerifiableContactDetailField,
  VerifiableContactDetailFieldBaseProps,
  VerifiableContactDetailFieldButtonProps,
  VerifiableContactDetailFieldInputProps,
  VerifiableContactDetailFieldProps,
} from './components/VerifiableContactDetailField';
import type { VerifiableContactDetail } from './types';
import { ValidatedVerifiableContactDetail } from './types';

type ControlledProps = {
  verifiedEmailAddresses?: VerifiableContactDetailFieldProps['verifiedContactDetails'];
  defaultVerifiedEmailAddresses?: never;
};

type UncontrolledProps = {
  verifiedEmailAddresses?: never;
  defaultVerifiedEmailAddresses?: VerifiableContactDetailFieldProps['defaultVerifiedContactDetails'];
};

export type VerifiableEmailFieldProps = (ControlledProps | UncontrolledProps) &
  VerifiableContactDetailFieldBaseProps & {
    inputProps?: Omit<VerifiableContactDetailFieldProps['inputProps'], 'label' | 'prefix'> & { label?: string };
    buttonProps?: Omit<VerifiableContactDetailFieldProps['buttonProps'], 'label'> & { label?: string };
    onChange: (next: ValidatedVerifiableContactDetail) => void;
    disabled?: boolean;
    validateEmailAddress?: (next?: string) => boolean;
    onVerifiedEmailAddressesChange?: VerifiableContactDetailFieldProps['onVerifiedContactDetailsChange'];
  };

const DEFAULT_INPUT_PROPS: VerifiableContactDetailFieldInputProps = {
  label: 'Email address',
  inputWidth: 'xl',
};

const DEFAULT_BUTTON_PROPS: VerifiableContactDetailFieldButtonProps = {
  label: 'Verify',
};

const normalizeEmailAddress = (email: string) => email?.toLowerCase()?.trim();

export const VerifiableEmailField = forwardRef<HTMLInputElement, VerifiableEmailFieldProps>((props, ref) => {
  const {
    value,
    inputProps: overrideInputProps,
    buttonProps: overrideButtonProps,
    onChange,
    validateEmailAddress = Validation.isValidEmail,
    verifiedEmailAddresses,
    defaultVerifiedEmailAddresses = [],
    onVerifiedEmailAddressesChange,
    disabled = false,
    ...propsRest
  } = props;

  const handleChange = ({ value, ...rest }: VerifiableContactDetail) => {
    onChange({
      ...rest,
      value: value,
      invalid: !validateEmailAddress(value),
    });
  };

  const inputProps = { ...DEFAULT_INPUT_PROPS, ...(overrideInputProps ?? {}) };
  const buttonProps = { ...DEFAULT_BUTTON_PROPS, ...(overrideButtonProps ?? {}) };
  const isValid = validateEmailAddress(value);

  return (
    <VerifiableContactDetailField
      ref={ref}
      type="email"
      value={value}
      inputProps={inputProps}
      buttonProps={buttonProps}
      enableVerify={isValid}
      onChange={handleChange}
      normalize={normalizeEmailAddress}
      verifiedContactDetails={verifiedEmailAddresses}
      defaultVerifiedContactDetails={defaultVerifiedEmailAddresses}
      onVerifiedContactDetailsChange={onVerifiedEmailAddressesChange}
      disabled={disabled}
      {...propsRest}
    />
  );
});
